/**
 * Who may edit which group (ORDR-119, ruling B10, seam C3 of w2c-ord-config).
 *
 * NO SINGLE CAPABILITY. Each domain is editable only with the capability that
 * already governs it; a domain absent from this table is READ-ONLY here.
 * Identical to the server's frozen table: a change is made in BOTH, and
 * `tables.test.ts` pins this to `orderingWriteCapability.fixture.ts`, the
 * server's table copied with its sha. BRAND_THEME was missing until ORDR-175,
 * so a brand admin holding `brand:manage-theme` saw the theme as read-only.
 *
 * HOURS takes `hours:manage` ALONE. B10's text says "hours:manage or
 * hours:manage-special", but the server accepts only the first (ORDR-134
 * ruling H2), and an enabled control the server refuses is a lie.
 *
 * A RENDERING decision. The server resolves the same rule on every write.
 */

import type { ConfigDomain } from "./domains.js";

export const WRITE_CAPABILITY: Readonly<Partial<Record<ConfigDomain, string>>> = {
  HOURS: "hours:manage",
  CAPACITY: "capacity:manage",
  PREP_TIMES: "prep-time:manage",
  STATION_ROUTING: "station:route",
  DELIVERY_ZONES: "delivery-zone:manage",
  DELIVERY_FEES: "delivery-fee:manage",
  BRAND_THEME: "brand:manage-theme",
};

/**
 * Why a group can or cannot be changed here (CF3). `editable` needs BOTH the
 * capability at the node AND the tree's `writable: true`; when it is false,
 * `reason` is the first that applies: no governing capability, the server's
 * own explanation of a lock, or the capability this person lacks.
 */
export function editability(
  domain: ConfigDomain,
  capabilities: readonly string[],
  writable: boolean,
  explanation: string,
  nodeName: string,
): { readonly editable: boolean; readonly reason: string | null } {
  const capability = WRITE_CAPABILITY[domain];
  if (capability === undefined) {
    return { editable: false, reason: "Read-only here: no capability governs this group in ordering." };
  }
  if (!writable) {
    return { editable: false, reason: explanation };
  }
  if (!capabilities.includes(capability)) {
    return { editable: false, reason: `You need ${capability} at ${nodeName} to change this.` };
  }
  return { editable: true, reason: null };
}
